import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Field } from 'react-formality'
import { IconButton, Tabs, Tab, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, AppBar, Toolbar, MenuItem, Select, InputLabel, FormControl } from '@material-ui/core'
import { MoreVert, Close } from '@material-ui/icons'
import classNames from 'classnames'
import linkTypes from './enums/linkTypes'
import stepTypes from './enums/stepTypes'
import stepStatusTypes from './enums/stepStatusTypes'
import connectPropTypes from './utilities/connectPropTypes'
import getProfileStatus from './utilities/Data/User/getProfileStatus'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import HeaderStatistic from './components/HeaderStatistic'
import styles from './User.icss'
import ContextMenu from './components/ContextMenu'
import RoundedImageIcon from './components/RoundedImageIcon'
import icons from './enums/icons'
import UserFit from './components/User/Fit'
import UserKit from './components/User/Kit'
import UserPosts from './components/User/Posts'
import UserSocial from './components/User/Social'
import Img from './components/Img'
import getField from './utilities/Data/getField'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'
import UploadImage from './components/UploadImage'
import messageTypes from './enums/messageTypes'
import errorTypes from './enums/errorTypes'
import AddButton from './components/AddButton'
import Form from './components/Form'
import relationshipTypes from './enums/relationshipTypes'

class User extends React.Component {
	constructor(props) {
		super(props)

		this.state = {
			showFullProfileImage: false,
			showWarningDialog: false,
			verifyDialogIsOpen: false
		}

		this.elementRefs = {}
		this.addRef = refId => (element) => {
			this.elementRefs[refId] = element
		}
		this.verifyForm = React.createRef()

		this.getUserProfile = this.getUserProfile.bind(this)
		this.handleEditStat = this.handleEditStat.bind(this)
		this.handleMakePrivateStat = this.handleMakePrivateStat.bind(this)
		this.handleMakePublicStat = this.handleMakePublicStat.bind(this)
		this.handleContextOpenForUserInfo = this.handleContextOpenForUserInfo.bind(this)
		this.handleContextCloseForUserInfo = this.handleContextCloseForUserInfo.bind(this)
		this.handleEditUserInfo = this.handleEditUserInfo.bind(this)
		this.renderFullNameContextMenu = this.renderFullNameContextMenu.bind(this)
		this.bindScrollToElement = this.bindScrollToElement.bind(this)
		this.renderTab = this.renderTab.bind(this)
		this.handleCloseFullProfileImage = this.handleCloseFullProfileImage.bind(this)
		this.handleCloseWarningDialog = this.handleCloseWarningDialog.bind(this)
		this.handleShowWarningDialog = this.handleShowWarningDialog.bind(this)
		this.handleDialogDelete = this.handleDialogDelete.bind(this)
		this.handleDialogDontDelete = this.handleDialogDontDelete.bind(this)
		this.handleFollow = this.handleFollow.bind(this)
		this.handleVerify = this.handleVerify.bind(this)
		this.handleUploadedImage = this.handleUploadedImage.bind(this)
		this.handleUploadImageError = this.handleUploadImageError.bind(this)
		this.getIconForType = this.getIconForType.bind(this)
		this.typeStatus = this.typeStatus.bind(this)
		this.getBadgeButtonText = this.getBadgeButtonText.bind(this)
		this.getVerificationsButtonText = this.getVerificationsButtonText.bind(this)
		this.getBasicInfoButtonText = this.getBasicInfoButtonText.bind(this)
		this.getYourFitButtonText = this.getYourFitButtonText.bind(this)
		this.getYourKitButtonText = this.getYourKitButtonText.bind(this)
		this.handleVerifyDialogOkay = this.handleVerifyDialogOkay.bind(this)
		this.handleVerifyDialogCancel = this.handleVerifyDialogCancel.bind(this)
		this.handleVerifyDialogSubmit = this.handleVerifyDialogSubmit.bind(this)
	}

	static getDerivedStateFromProps(props, state) {
		// only update on props change (let state changes go through)
		if (props === state.prevProps) {
			return null
		}

		const userName = props && props.match && props.match.params ?
			props.match.params.userName :
			null

		const parsedSearch = getNormalisedParameters(props.location.search)
		const uid = getDecodedURIComponent(parsedSearch.uid)

		// put following and verified in state to allow the ui to be optimistically updated
		const isFollowing = getField(props.userProfile, 'isFollowing')
		const hasVerified = getField(props.userProfile, 'hasVerified')
		const relationshipType = getField(props.userProfile, 'relationshipType')

		return { userName, uid, isFollowing, hasVerified, relationshipType, prevProps: props }
	}

	componentDidMount() {
		this.props.appBottomBarValue(linkTypes.USER)
		this.props.appBackButtonFunction(false)
		this.getUserProfile()
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.userName !== this.state.userName) {
			this.getUserProfile()
		} else if (this.props.userProfile && this.props.user) {
			if (this.props.heading && this.props.heading.text === null) {
				this.props.appHeading({ text: this.props.userProfile.fullName, icon: icons.whiteUser })
				this.props.appBottomBarValue(linkTypes.USER)
			}
			if (prevProps.userProfile !== this.props.userProfile || prevProps.user !== this.props.user) {
				const isOwnProfile = this.props.userProfile.uid === this.props.user.uid
				if (!isOwnProfile) {
					this.props.appAddContextMenu({
						menuKey: 'userFollowContextMenu',
						func: this.handleFollow,
						text: this.state.isFollowing ? this.context.translate('Unfollow') : this.context.translate('Follow')
					})
					if (!this.state.hasVerified) {
						this.props.appAddContextMenu({
							menuKey: 'userVerifyContextMenu',
							func: this.handleVerify,
							text: this.context.translate('Verify')
						})
					}
				}
			}
		}
	}

	getUserProfile() {
		this.props.appHeading({ text: this.context.translate('Loading...') })
		this.props.userProfileFetch({ userName: this.state.userName, uid: this.state.uid })
	}

	getBadgeIcon() {
		const statusIcons = {
			[stepStatusTypes.COMPLETED]: '/images/content/badges/full.svg',
			[stepStatusTypes.IN_PROGRESS]: '/images/content/badges/half.svg',
			[stepStatusTypes.INCOMPLETE]: '/images/content/badges/incomplete.svg'
		}
		const statuses = Object.values(stepTypes).map(type => getProfileStatus(this.props.userProfile, type))
		let status = stepStatusTypes.IN_PROGRESS
		if (statuses.every(s => s.completed)) {
			status = stepStatusTypes.COMPLETED
		}
		return statusIcons[status]
	}

	getIconForType(type) {
		const iconsForType = {
			[stepStatusTypes.COMPLETED]: '/images/content/badges/full.svg',
			[stepStatusTypes.IN_PROGRESS]: '/images/content/badges/half.svg',
			[stepStatusTypes.INCOMPLETE]: '/images/content/badges/incomplete.svg'
		}
		const myStatus = this.typeStatus(type)
		return iconsForType[myStatus.status]
	}

	getBadgeButtonText() {
		switch (this.allStatus().status) {
		case stepStatusTypes.COMPLETED:
			return this.context.translate('Well done, your profile is complete!')
		case stepStatusTypes.IN_PROGRESS:
			return this.context.translate('{percentage} Profile completeness', {
				percentage: {
					value: this.context.translate('{percent}%', { percent: Math.round(100 * this.allStatus().doneParts / this.allStatus().totalParts) }),
					display: value => <strong>{value}</strong>
				}
			})
		default:
			return this.context.translate('Complete your profile')
		}
	}

	getVerificationsButtonText() {
		switch (this.typeStatus(stepTypes.VERIFICATIONS).status) {
		case stepStatusTypes.COMPLETED:
			return this.context.translate('Verified')
		case stepStatusTypes.IN_PROGRESS:
			return this.context.translate('Get {number|# more verification|# more verifications}', { number: this.typeStatus(stepTypes.VERIFICATIONS).totalParts - this.typeStatus(stepTypes.VERIFICATIONS).doneParts })
		case stepStatusTypes.INCOMPLETE:
		default:
			return this.context.translate('Get verified')
		}
	}

	getBasicInfoButtonText() {
		switch (this.typeStatus(stepTypes.BASICINFO).status) {
		case stepStatusTypes.COMPLETED:
			return this.context.translate('Basic info')
		case stepStatusTypes.IN_PROGRESS:
		case stepStatusTypes.INCOMPLETE:
		default:
			return this.context.translate('Add basic info')
		}
	}

	getYourFitButtonText() {
		switch (this.typeStatus(stepTypes.YOURFIT).status) {
		case stepStatusTypes.COMPLETED:
			return this.context.translate('Basic fit complete')
		case stepStatusTypes.IN_PROGRESS:
		case stepStatusTypes.INCOMPLETE:
		default:
			return this.context.translate('Add info for better fit')
		}
	}

	getYourKitButtonText() {
		switch (this.typeStatus(stepTypes.YOURKIT).status) {
		case stepStatusTypes.COMPLETED:
			return this.context.translate('Basic kit complete')
		case stepStatusTypes.IN_PROGRESS:
		case stepStatusTypes.INCOMPLETE:
		default:
			return this.context.translate('Add your kit')
		}
	}

	typeStatus(type) {
		return getProfileStatus(this.props.userProfile, type)
	}

	allStatus() {
		const statuses = Object.values(stepTypes).map(type => getProfileStatus(this.props.userProfile, type))
		let status = stepStatusTypes.IN_PROGRESS
		if (statuses.every(s => s.completed)) {
			status = stepStatusTypes.COMPLETED
		}
		const completed = status === stepStatusTypes.COMPLETED
		const totalParts = statuses.reduce((total, partStatus) => total + partStatus.totalParts, 0)
		const doneParts = statuses.reduce((total, partStatus) => total + partStatus.doneParts, 0)
		return { status, completed, totalParts, doneParts }
	}

	handleEditStat(stat, statType) {
		const defaultLink = 'statistics'
		const linkableTypes = {
			weight: 'weight',
			waist: 'waist',
			height: 'height',
			chest: 'chest',
			chestShape: 'shape',
			fitPreference: 'shape',
			stomachShape: 'shape'
		}
		const linkPath = linkableTypes[statType] ? linkableTypes[statType] : defaultLink
		this.props.appRedirect(`/edit${linkPath}`)
	}

	handleMakePrivateStat(stat) {
		console.log('handleMakePrivateStat', this.state.userName, stat)
	}

	handleMakePublicStat(stat) {
		console.log('handleMakePublicStat', this.state.userName, stat)
	}

	handleContextCloseForUserInfo() {
		const showFullNameContextMenu = null
		this.setState({ showFullNameContextMenu })
	}

	handleContextOpenForUserInfo() {
		const showFullNameContextMenu = true
		this.setState({ showFullNameContextMenu })
	}

	handleEditUserInfo() {
		return () => {
			this.props.appRedirect('/editinfo')
		}
	}

	handleCloseFullProfileImage() {
		this.setState({ showFullProfileImage: false })
	}

	handleCloseWarningDialog() {
		this.setState({ showWarningDialog: false })
	}

	handleShowWarningDialog(warningTitle, warningContent, deleteFunc, deleteParams) {
		this.setState({ showWarningDialog: true, warningTitle, warningContent, deleteFunc, deleteParams })
	}

	bindWarningDialog({ title, content, func }) {
		return (...rest) => this.handleShowWarningDialog(title, content, func, rest)
	}

	handleDialogDelete() {
		this.state.deleteFunc(...this.state.deleteParams)
		this.handleCloseWarningDialog()
	}

	handleDialogDontDelete() {
		this.handleCloseWarningDialog()
	}

	handleFollow() {
		this.setState((state) => {
			const isFollowing = !state.isFollowing
			this.props.userProfileFollowersSet({ uid: this.props.userProfile.uid, isFollowing })
			return { isFollowing }
		})
	}

	handleVerify() {
		this.setState({ verifyDialogIsOpen: true })
	}

	handleVerifyDialogOkay() {
		this.verifyForm.current.submit()
	}

	handleVerifyDialogCancel() {
		this.setState({ verifyDialogIsOpen: false })
	}

	handleVerifyDialogSubmit(args) {
		if (args.isValid) {
			const { relationshipType } = args.values
			this.setState({ hasVerified: true, verifyDialogIsOpen: false, relationshipType })
			this.props.userProfileVerifiersSet({ uid: this.props.userProfile.uid, hasVerified: true, relationshipType })
		} else {
			this.props.appMessage(messageTypes.ERROR, this.context.translate('Please tell us how you know {userName}', { userName: this.state.userName }), errorTypes.LOCALISEDERROR)
		}
	}

	bindScrollToElement(refId) {
		return () => this.elementRefs[refId].scrollIntoView({ block: 'start', behavior: 'smooth' })
	}

	bindScrollToElementOrLinkIfIncomplete(refId, status, link) {
		if (status === stepStatusTypes.INCOMPLETE) {
			return () => {
				this.props.appRedirect(link)
			}
		}
		return this.bindScrollToElement(refId)
	}

	handleUploadedImage(url) {
		const profileImage = url
		this.props.userProfileUpdateProfilePicture({ profileImage })
		this.props.userProfileSetUploadedImage({ url })
	}

	handleUploadImageError() {
		this.props.appMessage(messageTypes.ERROR, this.context.translate('User profile picture upload failed'), errorTypes.UNKNOWNERROR)
	}

	renderTab() {
		switch (this.props.user.tab) {
		case 'fit':
			return (
				<UserFit
					userProfile={this.props.userProfile}
					ownUserProfile={this.props.user.userProfile}
					appRedirect={this.props.appRedirect}
				/>
			)
		case 'kit':
			return (
				<UserKit
					isOwnProfile={this.props.userProfile.uid === this.props.user.uid}
					userProfile={this.props.userProfile}
					userProfileKits={this.props.userProfileKits}
					userProfileKitsOwned={this.props.userProfileKitsOwned}
					userProfileKitsWanted={this.props.userProfileKitsWanted}
					userProfileItemsOwned={this.props.userProfileItemsOwned}
					userProfileItemsWanted={this.props.userProfileItemsWanted}
					appRedirect={this.props.appRedirect}
					kitDuplicate={this.props.kitDuplicate}
					kitDelete={this.bindWarningDialog({
						title: this.context.translate('Delete kit'),
						content: this.context.translate('Are you sure you want to delete this kit?'),
						func: this.props.kitDelete
					})}
					kitOwnSet={this.props.kitOwnSet}
					kitWantSet={this.props.kitWantSet}
					itemOwnSet={this.props.itemOwnSet}
					itemWantSet={this.props.itemWantSet}
				/>
			)
		case 'social':
			return (
				<UserSocial
					isOwnProfile={this.props.userProfile.uid === this.props.user.uid}
					userName={this.state.userName}
					userProfilePublicFollowing={this.props.userProfilePublicFollowing}
					userProfilePublicFollowers={this.props.userProfilePublicFollowers}
					userProfilePublicVerifiers={this.props.userProfilePublicVerifiers}
					userProfilePublicVerified={this.props.userProfilePublicVerified}
					userProfileFollowingRemove={this.props.userProfileFollowingRemove}
					userProfileFollowerRemove={this.props.userProfileFollowerRemove}
					userProfileFollowersSet={this.props.userProfileFollowersSet}
					countFollowers={getField(this.props.userProfile, 'countFollowers')}
					countFollowing={getField(this.props.userProfile, 'countFollowing')}
					countVerifiers={getField(this.props.userProfile, 'countVerifiers')}
					countVerified={getField(this.props.userProfile, 'countVerified')}
					hasVerified={this.state.hasVerified}
					verify={this.handleVerify}
				/>
			)
		case 'posts':
		default:
			return (
				<UserPosts
					isOwnProfile={this.props.userProfile.uid === this.props.user.uid}
					userProfile={this.props.userProfile}
					userProfilePosts={this.props.userProfilePosts}
					appRedirect={this.props.appRedirect}
					postDelete={this.bindWarningDialog({
						title: this.context.translate('Delete post'),
						content: this.context.translate('Are you sure you want to delete this post?'),
						func: this.props.postDelete
					})}
					postFollowersSet={this.props.postFollowersSet}
					itemOwnByKey={this.props.itemOwnByKey}
					kitOwnById={this.props.kitOwnById}
					itemWantByKey={this.props.itemWantByKey}
					kitWantById={this.props.kitWantById}
					itemOwnSet={this.props.itemOwnSet}
					kitOwnSet={this.props.kitOwnSet}
					itemWantSet={this.props.itemWantSet}
					kitWantSet={this.props.kitWantSet}
				/>
			)
		}
	}

	renderFullNameContextMenu() {
		const { showFullNameContextMenu } = this.state
		const anchorEl = this.elementRefs.editUserFullName

		if (showFullNameContextMenu && anchorEl) {
			return (
				<ContextMenu
					contextMenuItems={[{
						name: this.context.translate('Edit'),
						handler: this.handleEditUserInfo
					}]}
					handleContextClose={this.handleContextCloseForUserInfo}
					anchorEl={anchorEl}
					menuItemClickBinder={this.handleEditUserInfo}
					item={showFullNameContextMenu}
				/>
			)
		}
		return null
	}

	render() {
		if (!this.props.user) {
			return null
		}

		const userProfile = this.props.userProfile
		const isOwnProfile = userProfile && userProfile.uid === this.props.user.uid
		const upImages = userProfile && ((isOwnProfile && this.props.currentUserProfileImage) || userProfile.images || userProfile.profileImage)

		return (
			<div className={styles.wrapper}>
				{this.props.userNameNotFound && (
					<div>
						<h2>{this.context.translate('Not found')}</h2>
						<p>Sorry, {this.state.userName} was not found, maybe you can find them if you <Link to="/search">Search</Link>? </p>
					</div>
				)}
				{userProfile && (
					<div>
						<section className={styles.profileHeader}>
							<div className={styles.profileHeaderBadge}>
								<Img content={this.getBadgeIcon()} />
								<span className={styles.profileHeaderBadgeText}>{this.context.translate('{percent}%', { percent: Math.round(100 * this.allStatus().doneParts / this.allStatus().totalParts) })}</span>
							</div>
							<div className={styles.profileHeaderDetails}>
								<HeaderStatistic count={getField(this.props.userProfile, 'countPostsCreated')} name={this.context.translate('POSTS')} classNames={{ button: styles.headerStatisticButton }} />
								<HeaderStatistic count={getField(this.props.userProfile, 'countKitsCreated')} name={this.context.translate('KITS')} classNames={{ button: styles.headerStatisticButton }} />
								<HeaderStatistic count={getField(this.props.userProfile, 'countFollowers')} name={this.context.translate('FOLLOWERS')} classNames={{ button: styles.headerStatisticButton }} />
								<HeaderStatistic count={getField(this.props.userProfile, 'countFollowing')} name={this.context.translate('FOLLOWING')} classNames={{ button: styles.headerStatisticButton }} />
								<HeaderStatistic count={getField(this.props.userProfile, 'countVerifiers')} name={this.context.translate('VERIFIED')} classNames={{ button: styles.headerStatisticButton }} />
							</div>
						</section>

						<section className={classNames(styles.userDetails, styles.pagePadding)}>
							<div className={styles.userDetailsImage}>
								{isOwnProfile && !upImages ? (
									<UploadImage
										classNames={{
											surround: classNames(styles.profileImage, styles.uploadSurround),
											wrapper: styles.uploadWrapper,
											buttonRoot: styles.uploadButtonRoot,
											buttonIcon: styles.uploadButtonIcon,
											progress: styles.uploadProgress
										}}
										buttonIconName="whiteUser"
										hintText=""
										buttonText=""
										uploadingText={this.context.translate('Uploading')}
										uploadedText={this.context.translate('Done')}
										saveUploadedImage={this.handleUploadedImage}
										errorHandler={this.handleUploadImageError}
										imgInline={false}
									/>
								) : (
									<Button
										className={styles.compactButton}
										onClick={() => this.setState({ showFullProfileImage: true })}
									>
										<RoundedImageIcon
											className={upImages ? styles.profileImage : styles.profileImageMissing}
											content={upImages || icons.whiteUser}
											cover
											divisor={4}
										/>
									</Button>
								)}
								<Dialog
									fullScreen
									open={this.state.showFullProfileImage}
									onClose={this.handleCloseFullProfileImage}
								>
									<AppBar className={styles.appBar}>
										<Toolbar className={styles.toolBar}>
											<IconButton color="inherit" onClick={this.handleCloseFullProfileImage} aria-label="Close">
												<Close />
											</IconButton>
										</Toolbar>
									</AppBar>
									<div className={styles.fullDialog}>
										<RoundedImageIcon
											className={styles.profileImageLarge}
											content={upImages}
											cover
										/>
									</div>
								</Dialog>
							</div>
							<div className={styles.userDetailsInfo}>
								<div className={styles.userFullName}>
									<h2 className={styles.fullNameHeader}>{userProfile.fullName}</h2>
								</div>
								{userProfile.instagramHandle && (
									<div>
										{/* TODO: extent translation system to handle nested structure like this */}
										<span>{this.context.translate('Instagram {instagramHandle}', { instagramHandle: '' })}</span>
										<a target="_blank" rel="noopener noreferrer" href={`https://www.instagram.com/${userProfile.instagramHandle}`}>{`@${userProfile.instagramHandle}`}</a>
									</div>
								)}
								{userProfile.bio && (
									<div>
										{userProfile.bio}
									</div>
								)}
								{userProfile.link && (
									<div className={styles.userLink}>
										<a target="_blank" rel="noopener noreferrer" href={userProfile.link}>{userProfile.link}</a>
									</div>
								)}
								{isOwnProfile && !userProfile.bio && (
									<div className={styles.buttonLineRight}>
										<AddButton onClick={() => this.props.appRedirect('/editinfo')} className={styles.addButton}>{this.context.translate('Add Bio')}</AddButton>
									</div>
								)}
								{isOwnProfile && (
									<div className={styles.buttonLineLeft}>
										<Button variant="contained" className={classNames(styles.compactButton, styles.firstUserButton)} onClick={() => this.props.appRedirect('/addVerification')}>
											{this.context.translate('Get more verifications')}
										</Button>
									</div>
								)}
								{!isOwnProfile && (
									<div className={styles.buttonLineLeft}>
										<Button variant="contained" className={classNames(styles.compactButton, styles.firstUserButton, { [styles.buttonSelected]: this.state.hasVerified })} onClick={this.handleVerify}>
											{this.state.hasVerified ? this.context.translate('Verified') : this.context.translate('Verify')}
										</Button>
										<Button variant="contained" className={classNames(styles.compactButton, styles.userButton, { [styles.buttonSelected]: this.state.isFollowing })} onClick={this.handleFollow}>
											{this.state.isFollowing ? this.context.translate('Following') : this.context.translate('Follow')}
										</Button>
									</div>
								)}
							</div>
							{isOwnProfile && (
								<div>
									<IconButton className={styles.kebobMenu} aria-label="Context" onClick={this.handleContextOpenForUserInfo}>
										<span ref={this.addRef('editUserFullName')} />
										<MoreVert />
									</IconButton>
									{this.renderFullNameContextMenu()}
								</div>
							)}
						</section>

						{isOwnProfile && (
							<section className={classNames(styles.pagePadding, styles.buttonBoxSurround)}>
								<div className={styles.buttonBox}>
									<div className={styles.buttonSurround}>
										<Img content={this.getBadgeIcon()} />
										<span className={styles.buttonStepText}>
											{this.getBadgeButtonText()}
										</span>
									</div>
									<div className={styles.buttonSurround}>
										<Img content={this.getIconForType(stepTypes.VERIFICATIONS)} />
										<Button variant="contained" className={this.typeStatus(stepTypes.VERIFICATIONS).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete} onClick={() => this.props.appRedirect('/addVerification')}>
											{this.getVerificationsButtonText()}
										</Button>
									</div>
									<div className={styles.buttonSurround}>
										<Img content={this.getIconForType(stepTypes.BASICINFO)} />
										<Button variant="contained" className={this.typeStatus(stepTypes.BASICINFO).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete} onClick={() => this.props.appRedirect('/editinfo')}>
											{this.getBasicInfoButtonText()}
										</Button>
									</div>
									<div className={styles.buttonSurround}>
										<Img content={this.getIconForType(stepTypes.YOURFIT)} />
										<Button
											variant="contained"
											className={this.typeStatus(stepTypes.YOURFIT).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete}
											onClick={() => {
												this.props.userProfileTabSet({ tab: 'fit' })
												this.bindScrollToElement('tabs')()
											}}
										>
											{this.getYourFitButtonText()}
										</Button>
									</div>
									<div className={styles.buttonSurround}>
										<Img content={this.getIconForType(stepTypes.YOURKIT)} />
										<Button
											variant="contained"
											className={this.typeStatus(stepTypes.YOURKIT).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete}
											onClick={() => this.props.appRedirect('/postkit')}
										>
											{this.getYourKitButtonText()}
										</Button>
									</div>
								</div>
							</section>
						)}

						<span ref={this.addRef('tabs')} />
						<Tabs
							className={styles.tabs}
							value={this.props.user.tab || 'posts'}
							onChange={(event, value) => {
								this.props.userProfileTabSet({ tab: value })
							}}
							indicatorColor="secondary"
							variant="fullWidth"
						>
							<Tab label={this.context.translate('Posts')} value="posts" />
							<Tab label={this.context.translate('Kit')} value="kit" />
							<Tab label={this.context.translate('Fit')} value="fit" />
							<Tab label={this.context.translate('Social')} value="social" />
						</Tabs>
						{this.renderTab()}
						<Dialog
							open={this.state.showWarningDialog}
							onClose={this.handleCloseWarningDialog}
							aria-labelledby="alert-dialog-title"
							aria-describedby="alert-dialog-description"
						>
							<DialogTitle id="alert-dialog-title">{this.state.warningTitle}</DialogTitle>
							<DialogContent>
								<DialogContentText id="alert-dialog-description">
									{this.state.warningContent}
								</DialogContentText>
							</DialogContent>
							<DialogActions>
								<Button onClick={this.handleDialogDelete} color="primary">
									{this.context.translate('Delete')}
								</Button>
								<Button onClick={this.handleDialogDontDelete} color="primary" autoFocus>
									{this.context.translate('Don\'t delete')}
								</Button>
							</DialogActions>
						</Dialog>
					</div>
				)}
				<Dialog
					open={this.state.verifyDialogIsOpen}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
					fullWidth
				>
					<DialogTitle id="alert-dialog-title">{this.context.translate('Verify {userName}', { userName: this.state.userName })}</DialogTitle>
					<DialogContent>
						<Form
							onSubmit={this.handleVerifyDialogSubmit}
							ref={this.verifyForm}
							initialValues={{
								relationshipType: this.state.relationshipType || ''
							}}
						>
							{props => (
								<section className={styles.fieldSurround}>
									<Field
										id="alert-dialog-description"
										supportsHelperText={false}
										component={FormControl}
										name="relationshipType"
										validate={{
											presence: true
										}}
									>
										<InputLabel>{this.context.translate('How do you know them?')}</InputLabel>
										<Select
											value={props.values.relationshipType}
											onChange={props.store.setBinder('relationshipType')}
										>
											{Object.keys(relationshipTypes).map(relationshipType => <MenuItem key={relationshipType} value={relationshipType}>{relationshipTypes[relationshipType].name}</MenuItem>)}
										</Select>
									</Field>
								</section>
							)}
						</Form>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleVerifyDialogCancel} color="primary">
							{this.context.translate('Cancel')}
						</Button>
						<Button onClick={this.handleVerifyDialogOkay} color="primary" autoFocus>
							{this.context.translate('Okay')}
						</Button>
					</DialogActions>
				</Dialog>
			</div>
		)
	}
}

User.contextTypes = {
	translate: PropTypes.func
}

User.propTypes = {
	appAddContextMenu: PropTypes.func.isRequired,
	appBottomBarValue: PropTypes.func.isRequired,
	appBackButtonFunction: PropTypes.func.isRequired,
	appHeading: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	location: PropTypes.shape({
		search: PropTypes.string
	}).isRequired,
	user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
	userNameNotFound: PropTypes.bool,
	currentUserProfileImage: PropTypes.string,
	userProfile: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	userProfileFetch: PropTypes.func.isRequired,
	userProfileFollowersSet: PropTypes.func.isRequired,
	userProfileFollowingRemove: PropTypes.func.isRequired,
	userProfileFollowerRemove: PropTypes.func.isRequired,
	userProfileKits: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileKitsOwned: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileKitsWanted: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileItemsOwned: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileItemsWanted: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfilePosts: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfilePublicFollowing: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfilePublicFollowers: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfilePublicVerifiers: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfilePublicVerified: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileVerifiersSet: PropTypes.func.isRequired,
	kitDuplicate: PropTypes.func.isRequired,
	kitDelete: PropTypes.func.isRequired,
	kitOwnSet: PropTypes.func.isRequired,
	kitWantSet: PropTypes.func.isRequired,
	itemOwnSet: PropTypes.func.isRequired,
	itemWantSet: PropTypes.func.isRequired,
	postDelete: PropTypes.func.isRequired,
	postFollowersSet: PropTypes.func.isRequired,
	itemOwnByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	heading: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitOwnById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemWantByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitWantById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	userProfileTabSet: PropTypes.func.isRequired,
	userProfileUpdateProfilePicture: PropTypes.func.isRequired,
	userProfileSetUploadedImage: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired
}

User.defaultProps = {
	userNameNotFound: false,
	userProfile: undefined,
	currentUserProfileImage: undefined,
	userProfileKits: undefined,
	userProfilePosts: undefined,
	userProfileKitsOwned: undefined,
	userProfileKitsWanted: undefined,
	userProfileItemsOwned: undefined,
	userProfileItemsWanted: undefined,
	userProfilePublicFollowing: undefined,
	userProfilePublicFollowers: undefined,
	userProfilePublicVerifiers: undefined,
	userProfilePublicVerified: undefined,
	heading: undefined,
	itemOwnByKey: undefined,
	kitOwnById: undefined,
	itemWantByKey: undefined,
	kitWantById: undefined
}

export const testing = {
	User
}

export default connectPropTypes(User)



// WEBPACK FOOTER //
// ./src/User.jsx